import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Loader2, LinkIcon, CheckCircle, Search, X, Filter, Ban, CheckSquare, Square } from "lucide-react";
import api from "../../lib/api";
import toast from "react-hot-toast";
import Pagination from "../ui/pagination";
import ProductMappingModal from "./ProductMappingModal";
import CreateProductModal from "./CreateProductModal";
interface B2CEntityProduct {
  id: string;
  hsn: string;
  mrp: string;
  pack: string;
  name: string;
  mfr: string;
  gstp: number;
  branch_id: number;
  tp_code: string | null;
  med_products_id: number | null;
  code: string;
}

interface UpmProduct {
  sku_id: number;
  sku_name: string;
  sku_sub_category?: string;
  brand: string;
  manufacturer_name: string;
  mrp_price: number;
}

interface LocalMapping {
  entityProductId: string;
  upmProductId: number;
  productName: string;
  upmProduct: UpmProduct;
}

interface UnmappedB2CProductsSimpleProps {
  entities: any;
  selectedEntity?: any;
  onEntityChange?: (entity: any) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  showOnlyOtc?: boolean;
  showMrpZero?: boolean;
  onStatisticsUpdate?: () => void;
}

const UnmappedB2CProductsSimple = ({
  entities,
  selectedEntity: parentSelectedEntity,
  onEntityChange,
  page: parentPage,
  onPageChange,
  showOnlyOtc,
  showMrpZero,
  onStatisticsUpdate,
}: UnmappedB2CProductsSimpleProps) => {
  const [products, setProducts] = useState<B2CEntityProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(parentPage || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState({ mapped: 0, unmapped: 0 });
  const [selectedEntity, setSelectedEntity] = useState<any>(
    parentSelectedEntity || null
  );

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [showMRPZero, setShowMRPZero] = useState(showMrpZero || false);
  const [filterType, setFilterType] = useState<'unmapped' | 'mapped'>('unmapped');

  // Modal states
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<B2CEntityProduct | null>(null);
  const [showNewProductModal, setShowNewProductModal] = useState(false);
  // Local mappings state
  const [localMappings, setLocalMappings] = useState<LocalMapping[]>([]);
  const [savingMappings, setSavingMappings] = useState(false);

  // Bulk selection and actions
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const fetchProducts = useCallback(async () => {
    // Allow null entity to show all products
    setLoading(true);
    try {
      const response = await api.getB2CMappedProducts(
        page,
        20,
        filterType,
        selectedEntity,
        showOnlyOtc || false,
        searchQuery,
        showMRPZero
      );
      if (response.success) {
        setProducts(response.data);
        setTotalPages(response.pagination.totalPages);
        if (response.counts) {
          setTotalCount(response.counts);
        }
      } else {
        toast.error("Failed to fetch products");
      }
    } catch (error) {
      toast.error("Error fetching products");
    } finally {
      setLoading(false);
    }
  }, [page, filterType, selectedEntity, showOnlyOtc, searchQuery, showMRPZero]);

  useEffect(() => {
    if (parentSelectedEntity !== undefined) {
      setSelectedEntity(parentSelectedEntity);
    }
  }, [parentSelectedEntity]);

  useEffect(() => {
    if (parentPage !== undefined) {
      setPage(parentPage);
    }
  }, [parentPage]);

  useEffect(() => {
    if (showMrpZero !== undefined) {
      setShowMRPZero(showMrpZero);
    }
  }, [showMrpZero]);

  useEffect(() => {
    // Always fetch products (null entity means all products)
    fetchProducts();
  }, [fetchProducts]);

  const handleEntityChange = (entity: any) => {
    setSelectedEntity(entity);
    if (onEntityChange) {
      onEntityChange(entity);
    }
    setPage(1);
    if (onPageChange) {
      onPageChange(1);
    }
    // Clear local mappings and search when entity changes
    setLocalMappings([]);
    setSearchQuery("");
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleOpenMappingModal = (product: B2CEntityProduct) => {
    setSelectedProduct(product);
    setShowMappingModal(true);
  };

  const handleOpenNewProductModal = () => {
    setShowMappingModal(false);
    setShowNewProductModal(true);
  };

  const handleProductLinked = (entityProductId: string, upmProduct: UpmProduct) => {
    // Product has already been saved to database in the modal
    // Just refresh the products list
    fetchProducts();
    // Refresh statistics to update counts now that the DB transaction has completed
    if (onStatisticsUpdate) {
      onStatisticsUpdate();
    }
  };

  const saveAllMappings = async () => {
    if (localMappings.length === 0) return;

    setSavingMappings(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const mapping of localMappings) {
        try {
          const response = await api.createProductLink(
            mapping.entityProductId,
            mapping.upmProductId.toString()
          );

          if (response.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully saved ${successCount} mapping${successCount > 1 ? 's' : ''}`);
      }

      if (errorCount > 0) {
        toast.error(`Failed to save ${errorCount} mapping${errorCount > 1 ? 's' : ''}`);
      }

      // Clear local mappings on success
      if (successCount > 0) {
        setLocalMappings([]);
        fetchProducts(); // Refresh the list
        // Refresh statistics to update counts now that the DB transaction has completed
        if (onStatisticsUpdate) {
          onStatisticsUpdate();
        }
      }
    } catch (error) {
      toast.error("Error saving mappings");
    } finally {
      setSavingMappings(false);
    }
  };

  const removeLocalMapping = (entityProductId: string) => {
    setLocalMappings(prev => prev.filter(m => m.entityProductId !== entityProductId));
    // Add back to products list
    fetchProducts();
  };

  // Check if product has local mapping
  const hasLocalMapping = (productId: string) => {
    return localMappings.some(m => m.entityProductId === productId);
  };

  const getLocalMapping = (productId: string) => {
    return localMappings.find(m => m.entityProductId === productId);
  };

  // Bulk selection handlers
  const handleSelectProduct = (productId: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  // Move products to no match found
  const handleMoveToNoMatchFound = async (productIds?: string[]) => {
    const idsToMove = productIds || Array.from(selectedProducts);

    if (idsToMove.length === 0) {
      toast.error("Please select products to move");
      return;
    }

    setBulkActionLoading(true);
    try {
      const response = await api.moveProductsToNoMatchFound(idsToMove);

      if (response?.success) {
        toast.success(`${idsToMove.length} products moved to No Match Found`);
        setSelectedProducts(new Set());

        // Immediately remove the moved products from the current list
        setProducts(prevProducts =>
          prevProducts.filter(product => !idsToMove.includes(product.id))
        );

        // Refresh statistics to update counts now that the DB transaction has completed
        if (onStatisticsUpdate) {
          onStatisticsUpdate();
        }

        // Optionally refetch to ensure consistency with backend
        // fetchProducts();
      } else {
        toast.error(response?.error || "Failed to move products");
      }
    } catch (error) {
      toast.error("Failed to move products to No Match Found");
    } finally {
      setBulkActionLoading(false);
    }
  };

  return (
    <>
      <div className="">

        {/* Search and Filter Bar */}
        <div className="px-6 py-3 bg-gray-700 dark:bg-gray-800">
          <div className="flex items-center gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search products by name or code..."
                className="w-full pl-10 pr-10 py-2.5 bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 rounded-lg"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleClearSearch();
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Clear Button */}
            {searchQuery && (
              <Button
                variant="outline"
                onClick={handleClearSearch}
                className="border-gray-500 text-gray-300 hover:bg-gray-600 bg-gray-700"
              >
                Clear
              </Button>
            )}

            {/* Show MRP ₹0 Button moved to parent */}
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedProducts.size > 0 && (
          <div className="px-6 py-3 bg-gray-800 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge variant="secondary" className="bg-blue-600 text-white">
                  {selectedProducts.size} product{selectedProducts.size > 1 ? 's' : ''} selected
                </Badge>
                <Button
                  onClick={() => setSelectedProducts(new Set())}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear Selection
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMoveToNoMatchFound();
                  }}
                  disabled={bulkActionLoading}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {bulkActionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Ban className="w-4 h-4 mr-2" />
                  )}
                  Move to No Match Found
                </Button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-64 bg-gray-800">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
            <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-700 border-b border-gray-600">
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-2 w-8">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAll}
                        className="h-6 w-6 p-0"
                      >
                        {selectedProducts.size === products.length ? (
                          <CheckSquare className="w-4 h-4" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                      </Button>
                    </TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6">Product Code</TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6">Product Name</TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6">MRP</TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6">Packing</TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6">Manufacturer</TableHead>
                    <TableHead className="text-gray-300 font-semibold uppercase text-xs py-3 px-6 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                  <TableBody>
                    {products.map(product => {
                      const localMapping = getLocalMapping(product.id);
                      const hasPendingMapping = hasLocalMapping(product.id);

                      return (
                      <TableRow
                        key={product.id}
                        className={`border-b border-gray-700 hover:bg-gray-700/50 ${
                          hasPendingMapping ? "bg-green-900/20" : ""
                        }`}
                      >
                        <TableCell className="py-3 px-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSelectProduct(product.id)}
                            className="h-6 w-6 p-0"
                          >
                            {selectedProducts.has(product.id) ? (
                              <CheckSquare className="w-4 h-4 text-blue-400" />
                            ) : (
                              <Square className="w-4 h-4 text-gray-400" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="text-gray-300 font-mono py-3 px-6">
                          {product.code}
                        </TableCell>
                        <TableCell className="text-gray-100 font-medium py-3 px-6">
                            {product.name}
                            {hasPendingMapping && localMapping && (
                              <div className="text-xs text-green-400 mt-1">
                                → {localMapping.productName}
                              </div>
                            )}
                        </TableCell>
                        <TableCell className="text-gray-100 py-3 px-6">
                          ₹{product.mrp}
                        </TableCell>
                        <TableCell className="text-gray-300 py-3 px-6">
                          {product.pack}
                        </TableCell>
                        <TableCell className="text-gray-300 py-3 px-6">
                          {product.mfr}
                        </TableCell>
                        <TableCell className="text-center py-3 px-6">
                          <div className="flex items-center gap-2 justify-center">
                            {hasPendingMapping ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeLocalMapping(product.id)}
                                className="bg-red-600 hover:bg-red-700 text-white border-0"
                              >
                                Remove
                              </Button>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleOpenMappingModal(product)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 text-xs h-7 flex items-center gap-1"
                                >
                                  <LinkIcon className="w-3 h-3" />
                                  Link
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleMoveToNoMatchFound([product.id]);
                                  }}
                                  disabled={bulkActionLoading}
                                  variant="outline"
                                  className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white px-2 py-1 text-xs h-7 flex items-center gap-1 whitespace-nowrap"
                                >
                                  <Ban className="w-3 h-3" />
                                  No Match
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

            {products.length === 0 && (
              <div className="text-center py-8 text-gray-400 bg-gray-800">
                {searchQuery ? "No products found matching your search" : "No unmapped products found"}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-center py-4 bg-gray-700 border-t border-gray-600">
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Product Mapping Modal */}
      <ProductMappingModal
        isOpen={showMappingModal}
        onClose={() => {
          setShowMappingModal(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        onProductLinked={handleProductLinked}
        openNewProductModal={handleOpenNewProductModal}
        showOnlyOtc={showOnlyOtc}
      />
       {/* New Product Modal */}
      <CreateProductModal
        product={selectedProduct}
        isOpen={showNewProductModal}
        onClose={() => {
          setShowNewProductModal(false);
          setSelectedProduct(null);
        }}
        onProductLinked={handleProductLinked}
      />
    </>
  );
};

export default UnmappedB2CProductsSimple;
